import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { usePanelPreferenceStore } from '@kc-console/context';

import { SplitDetailPanelStoreContext } from '../context';
import { createSplitDetailPanelStore } from '../store/splitDetailPanelStore';

import type { PropsWithChildren } from 'react';
import type { StoreApi } from 'zustand';
import type { SplitDetailPanelStore } from '../types';

type PanelTypeLabel = 'bottom' | 'right';

interface SplitDetailPanelProviderProps extends PropsWithChildren {
  maxTabs?: number;
  pageKey?: string;
}

// Extended context value with panel preferences
export interface SplitDetailPanelContextValue {
  availablePanel: boolean;
  enablePanel: boolean;
  maxTabs: number;
  panelSize: { height?: number; width?: number };
  panelType: PanelTypeLabel;
  setEnablePanel: (enable: boolean) => void;
  setPanelSize: (size: { height?: number; width?: number }) => void;
  setPanelType: (type: PanelTypeLabel) => void;
  store: StoreApi<SplitDetailPanelStore>;
}

/**
 * SplitDetailPanelProvider
 *
 * Creates a zustand store instance for managing detail panel tabs.
 * Uses React Context to provide the store to child components.
 *
 * Features:
 * - Instance-based store per Provider (not global singleton)
 * - Configurable max tabs limit (default: 5)
 * - Tab lifecycle management (open, close, switch, reconcile)
 * - Automatic visibility management based on tab count
 *
 * @param pageKey - Unique identifier for devtools (optional)
 * @param maxTabs - Maximum number of open tabs (default: 5)
 * @param children - Child components (main content area)
 *
 * @example
 * ```tsx
 * <SplitDetailPanelProvider maxTabs={5} pageKey="dns-zones">
 *   <ZoneDetail />
 * </SplitDetailPanelProvider>
 * ```
 */
export default function SplitDetailPanelProvider({
  pageKey,
  maxTabs = 1,
  children,
}: SplitDetailPanelProviderProps) {
  const storeRef = useRef<StoreApi<SplitDetailPanelStore> | null>(null);

  if (!storeRef.current) {
    storeRef.current = createSplitDetailPanelStore(maxTabs, pageKey);
  }

  // Panel preferences state
  const [panelPreferences, setPanelPreferences] = useState({
    enablePanel: false,
    availablePanel: true,
    panelType: 'right' as PanelTypeLabel,
    panelSize: { height: 600, width: 1000 },
  });
  const detailEnabled = usePanelPreferenceStore(state => state.preferences.detail.enabled);
  const detailAvailable = usePanelPreferenceStore(state => state.availability.detail);
  const canShowDetailPanel = detailEnabled && detailAvailable;

  useEffect(() => {
    if (canShowDetailPanel) return;

    storeRef.current?.getState().reset();
  }, [canShowDetailPanel]);

  // Cleanup on unmount
  useEffect(() => {
    const store = storeRef.current;
    return () => {
      if (store) {
        store.getState().reset();
      }
    };
  }, []);

  // Methods to update panel preferences
  const setPanelType = useCallback((type: PanelTypeLabel) => {
    setPanelPreferences(prev => ({ ...prev, panelType: type }));
  }, []);

  const setPanelSize = useCallback((size: { height?: number; width?: number }) => {
    setPanelPreferences(prev => ({
      ...prev,
      panelSize: {
        height: size.height ?? prev.panelSize.height,
        width: size.width ?? prev.panelSize.width,
      },
    }));
  }, []);

  const setEnablePanel = useCallback((enable: boolean) => {
    setPanelPreferences(prev => ({ ...prev, enablePanel: enable }));
  }, []);

  const contextValue = useMemo<SplitDetailPanelContextValue>(() => {
    if (!storeRef.current) {
      throw new Error('Store not initialized');
    }

    return {
      maxTabs,
      store: storeRef.current,
      enablePanel: panelPreferences.enablePanel,
      availablePanel: panelPreferences.availablePanel,
      panelType: panelPreferences.panelType,
      panelSize: panelPreferences.panelSize,
      setPanelType,
      setPanelSize,
      setEnablePanel,
    };
  }, [
    maxTabs,
    panelPreferences.enablePanel,
    panelPreferences.availablePanel,
    panelPreferences.panelType,
    panelPreferences.panelSize,
    setPanelType,
    setPanelSize,
    setEnablePanel,
  ]);

  return (
    <SplitDetailPanelStoreContext.Provider value={contextValue}>
      <SplitDetailPanelContent>{children}</SplitDetailPanelContent>
    </SplitDetailPanelStoreContext.Provider>
  );
}

interface SplitDetailPanelContentProps extends PropsWithChildren {}

function SplitDetailPanelContent({ children }: SplitDetailPanelContentProps) {
  return <>{children}</>;
}
